import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { InstanceBrandingProvider } from "./components/instance/InstanceBrandingProvider";
import { WalletAuth0Provider } from "./lib/auth0";
import { installGlobalBrowserErrorHandlers } from "./lib/global-error-handlers";
import { isAdminPathname, isAuthTransitionPathname, normalizeAppPathname } from "./lib/route-path";
import { hasBundledInstanceBranding, isInstanceBrandingRequired } from "./lib/runtime-mode";
import { getRouter } from "./router";
import "./styles.css";

installGlobalBrowserErrorHandlers();

const rootEl = document.getElementById("root");

if (!rootEl) {
  throw new Error("Root element #root not found");
}

function canonicalizeAdminPath() {
  if (typeof window === "undefined") return;
  const pathname = window.location.pathname || "/";
  if (!isAdminPathname(pathname)) return;

  const canonicalPathname = normalizeAppPathname(pathname);
  if (canonicalPathname === pathname) return;

  window.history.replaceState(
    window.history.state,
    "",
    `${canonicalPathname}${window.location.search || ""}${window.location.hash || ""}`,
  );
}

canonicalizeAdminPath();

const router = getRouter();
const app = <RouterProvider router={router} />;
const routedApp = isAdminPathname(window.location.pathname) ? (
  app
) : (
  <WalletAuth0Provider>{app}</WalletAuth0Provider>
);
const brandedApp =
  isAuthTransitionPathname(window.location.pathname) && !hasBundledInstanceBranding() ? (
    routedApp
  ) : (
    <InstanceBrandingProvider required={isInstanceBrandingRequired()}>
      {routedApp}
    </InstanceBrandingProvider>
  );

createRoot(rootEl).render(
  <StrictMode>
    <AppErrorBoundary>{brandedApp}</AppErrorBoundary>
  </StrictMode>,
);
